import { Suspense, useRef, useMemo } from "react";
import { Canvas, useFrame, useLoader } from "@react-three/fiber";
import { Float, Environment } from "@react-three/drei";
import * as THREE from "three";

const PATCH_TEX = "/assets/lifewave_patch.png";

function RotatingPatch() {
  const ref = useRef();
  const haloRef = useRef();
  const texture = useLoader(THREE.TextureLoader, PATCH_TEX);

  useMemo(() => {
    texture.colorSpace = THREE.SRGBColorSpace;
    texture.anisotropy = 16;
    texture.minFilter = THREE.LinearMipmapLinearFilter;
    texture.magFilter = THREE.LinearFilter;
  }, [texture]);

  useFrame(({ clock }) => {
    const t = clock.elapsedTime;
    if (ref.current) {
      // Continuous 360° turn on Y, slight wobble on X
      ref.current.rotation.y = t * 0.8;
      ref.current.rotation.x = Math.sin(t * 0.5) * 0.18;
    }
    if (haloRef.current) {
      haloRef.current.material.opacity = 0.35 + Math.sin(t * 1.5) * 0.12;
      haloRef.current.scale.setScalar(1 + Math.sin(t * 1.2) * 0.04);
    }
  });

  return (
    <Float speed={1.4} rotationIntensity={0.1} floatIntensity={0.7}>
      <group ref={ref}>
        {/* Soft cyan glow halo */}
        <mesh ref={haloRef} position={[0, 0, -0.08]}>
          <circleGeometry args={[1.55, 64]} />
          <meshBasicMaterial color="#00F0FF" transparent opacity={0.4} depthWrite={false} />
        </mesh>
        {/* Outer secondary halo */}
        <mesh position={[0, 0, -0.12]}>
          <circleGeometry args={[2.2, 64]} />
          <meshBasicMaterial color="#00F0FF" transparent opacity={0.12} depthWrite={false} />
        </mesh>
        {/* The real LifeWave patch (double-sided so 360° rotation shows it from any angle) */}
        <mesh>
          <planeGeometry args={[2.4, 2.4]} />
          <meshBasicMaterial
            map={texture}
            transparent
            side={THREE.DoubleSide}
            toneMapped={false}
            depthWrite={false}
          />
        </mesh>
      </group>
    </Float>
  );
}

function StagePedestal() {
  return (
    <group position={[0, -1.7, 0]}>
      <mesh rotation={[Math.PI / 2, 0, 0]}>
        <ringGeometry args={[1.6, 1.85, 64]} />
        <meshBasicMaterial color="#00F0FF" transparent opacity={0.55} side={THREE.DoubleSide} />
      </mesh>
      <mesh rotation={[Math.PI / 2, 0, 0]} position={[0, -0.02, 0]}>
        <ringGeometry args={[1.95, 2.35, 64]} />
        <meshBasicMaterial color="#D900FF" transparent opacity={0.3} side={THREE.DoubleSide} />
      </mesh>
    </group>
  );
}

export default function X39Patch() {
  return (
    <Canvas camera={{ position: [0, 0.2, 5.2], fov: 45 }} dpr={[1, 1.8]}>
      <ambientLight intensity={0.9} />
      <pointLight position={[3, 4, 4]} color="#00F0FF" intensity={2} />
      <pointLight position={[-3, -2, 3]} color="#D900FF" intensity={1.2} />
      <Suspense fallback={null}>
        <Environment preset="city" />
        <RotatingPatch />
        <StagePedestal />
      </Suspense>
    </Canvas>
  );
}
